import { useCallback, useEffect, useState } from "react";
import {
  FORMAT_ERROR,
  isAllowedBatchPersonFile,
  parseBatchPersonFile,
} from "./parseBatchPersonFile";
import {
  collectOrganizationCpfs,
  fetchPersonsByOrganization,
  registerPersonsInBatches,
} from "./personBatchRegisterService";
import type {
  BatchPersonFailure,
  BatchPersonSummary,
  PersonBatchStep,
} from "./types";
import { buildBatchPersonSummary } from "./buildBatchPersonSummary";

export function usePersonBatchRegister(organizationId?: string) {
  const [step, setStep] = useState<PersonBatchStep>("upload");
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [summary, setSummary] = useState<BatchPersonSummary | null>(null);
  const [progressDone, setProgressDone] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [failures, setFailures] = useState<BatchPersonFailure[]>([]);
  const [successCount, setSuccessCount] = useState(0);

  const isRegistering = step === "progress";

  const reset = useCallback(() => {
    setStep("upload");
    setFileName("");
    setFileError("");
    setIsChecking(false);
    setSummary(null);
    setProgressDone(0);
    setProgressTotal(0);
    setFailures([]);
    setSuccessCount(0);
  }, []);

  const handleFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setFileError("");
      setSummary(null);
      setFileName(file.name);

      if (!isAllowedBatchPersonFile(file)) {
        setFileError(FORMAT_ERROR);
        setStep("upload");
        return;
      }

      const text = await file.text();
      const parsed = parseBatchPersonFile(file, text);
      if (!parsed.ok) {
        setFileError(parsed.message);
        setStep("upload");
        return;
      }

      if (!organizationId) {
        setFileError("Não foi possível identificar a clínica. Tente novamente.");
        return;
      }

      setIsChecking(true);
      setStep("summary");
      try {
        const persons = await fetchPersonsByOrganization(organizationId);
        const orgCpfs = collectOrganizationCpfs(persons);
        setSummary(buildBatchPersonSummary(parsed.rows, orgCpfs, parsed.usedFlexibleMapping));
      } catch (error) {
        console.error("Cadastro em lote: falha ao verificar cadastrados");
        setFileError("Não foi possível verificar quem já está cadastrado. Tente de novo.");
        setStep("upload");
      } finally {
        setIsChecking(false);
      }
    },
    [organizationId],
  );

  const startRegister = useCallback(async () => {
    if (!organizationId || !summary?.toRegister.length || isRegistering) return;
    const queue = summary.toRegister;
    setStep("progress");
    setProgressDone(0);
    setProgressTotal(queue.length);
    const nextFailures = await registerPersonsInBatches(
      queue,
      organizationId,
      (done, total) => {
        setProgressDone(done);
        setProgressTotal(total);
      },
    );
    setFailures(nextFailures);
    setSuccessCount(queue.length - nextFailures.length);
    setStep("result");
  }, [organizationId, summary, isRegistering]);

  const retryFailures = useCallback(async () => {
    if (!organizationId || failures.length === 0 || isRegistering) return;
    const queue = failures.map((item) => item.row);
    setStep("progress");
    setProgressDone(0);
    setProgressTotal(queue.length);
    const nextFailures = await registerPersonsInBatches(
      queue,
      organizationId,
      (done, total) => {
        setProgressDone(done);
        setProgressTotal(total);
      },
    );
    setFailures(nextFailures);
    setSuccessCount((previous) => previous + (queue.length - nextFailures.length));
    setStep("result");
  }, [organizationId, failures, isRegistering]);

  useEffect(() => {
    if (!isRegistering) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isRegistering]);

  return {
    step,
    fileName,
    fileError,
    isChecking,
    isRegistering,
    summary,
    progressDone,
    progressTotal,
    failures,
    successCount,
    handleFile,
    startRegister,
    retryFailures,
    reset,
  };
}
